import React, { useState, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../../contexts/AuthContext';
import { APP_ERROR_MESSAGES, VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { redirectBasedOnPermission } from '../../../../utils/methods';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import AuthService from '../../../../services/auth.service';
import { Modules, NonAuthRoutes, RoleCodes } from '../../../../enums';
import { validateLoginForm } from '../validations';
import { useNavigate } from 'react-router-dom';

type Step = 'email' | 'code';

const Form: React.FC = () => {
  const { setLoggedInUser, setToken, setPermissions, setIsSystemAdmin } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [apiError, setApiError] = useState('');

  const handleEmailChange = useCallback((event: any) => {
    setEmail(event.target.value);
    setEmailError('');
    setApiError('');
  }, []);

  const handleEmailBlur = useCallback((event: any) => {
    const result = validateLoginForm({ email: event.target.value.trim() }, VALIDATION_FIELD_MAPPING.email);
    const field = result.fields.find((f) => f.field === VALIDATION_FIELD_MAPPING.email);
    setEmailError(field?.message ?? '');
  }, []);

  const handleSendCode = useCallback(
    async (event?: React.FormEvent) => {
      event && event.preventDefault();
      const result = validateLoginForm({ email: email.trim() });
      if (!result.validate) {
        const field = result.fields.find((f) => f.field === VALIDATION_FIELD_MAPPING.email);
        setEmailError(field?.message ?? APP_ERROR_MESSAGES.login.blankUsername);
        return;
      }
      setEmailError('');
      setApiError('');
      setLoading(true);
      try {
        await AuthService.requestLoginOtp(email.trim());
        setStep('code');
      } catch {
        // Anti-enumeration: always advance to code step
        setStep('code');
      } finally {
        setLoading(false);
      }
    },
    [email]
  );

  const handleVerify = useCallback(
    async (event?: React.FormEvent) => {
      event && event.preventDefault();
      const trimmedCode = code.trim();
      if (!trimmedCode || !/^\d{6}$/.test(trimmedCode)) {
        setCodeError(t('login.pageTexts.otpMode.code.errors.invalid'));
        return;
      }
      setCodeError('');
      setApiError('');
      setLoading(true);
      try {
        const response: any = await AuthService.verifyLoginOtp(email.trim(), trimmedCode);
        if (response.status === 'success') {
          const data = response.data;

          if (data.requiresMfa) {
            navigate(Modules.auth + NonAuthRoutes.mfaVerify, {
              state: { userId: data.userId, mfaMethod: data.mfaMethod },
            });
            return;
          }

          const user = data.user ?? {};
          const userPermissions = user.permissions ?? [];
          const isSystemAdmin = user.role === RoleCodes.admin;

          setLoggedInUser(user);
          setToken(data.token ?? '');
          setPermissions(userPermissions);
          setIsSystemAdmin(isSystemAdmin);

          navigate(
            redirectBasedOnPermission(isSystemAdmin, userPermissions) ||
              Modules.error + NonAuthRoutes.forbidden
          );
        }
      } catch (err: any) {
        setApiError(err?.response?.data?.message ?? t('login.pageTexts.otpMode.code.errors.invalid'));
      } finally {
        setLoading(false);
      }
    },
    [code, email, setLoggedInUser, setToken, setPermissions, setIsSystemAdmin, navigate, t]
  );

  const handleResend = useCallback(async () => {
    setResending(true);
    setApiError('');
    try {
      await AuthService.requestLoginOtp(email.trim());
    } finally {
      setResending(false);
    }
  }, [email]);

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (step === 'email') handleSendCode();
        else handleVerify();
      }
    },
    [step, handleSendCode, handleVerify]
  );

  if (step === 'code') {
    return (
      <form id="formAuthentication" className="mb-4" autoComplete="off" onSubmit={handleVerify}>
        <p className="text-muted small mb-4">{t('login.pageTexts.otpMode.codeSentMessage')}</p>
        <div className="mb-4">
          <Input
            type="text"
            id="otp-code"
            name="code"
            error={!!codeError}
            errorMessage={codeError}
            label={t('login.pageTexts.otpMode.code.label')}
            placeholder={t('login.pageTexts.otpMode.code.placeholder')}
            value={code}
            onKeyDown={handleKeyDown}
            onBlur={() => {}}
            onChange={(_e: any) => { setCode(_e.target.value); setCodeError(''); }}
          />
        </div>
        {apiError && (
          <div className="mb-3">
            <p className="text-danger small mb-0">{apiError}</p>
          </div>
        )}
        <Button
          className="btn btn-primary d-grid w-100 mb-3"
          text={t('login.pageTexts.otpMode.verifyButton')}
          disabled={loading}
          loading={loading}
          onClick={handleVerify}
        />
        <Button
          className="btn btn-outline-secondary d-grid w-100"
          text={t('login.pageTexts.otpMode.resendButton')}
          disabled={resending}
          loading={resending}
          onClick={handleResend}
        />
      </form>
    );
  }

  return (
    <form id="formAuthentication" className="mb-4" autoComplete="off" onSubmit={handleSendCode}>
      <div className="mb-4">
        <Input
          type="text"
          id={VALIDATION_FIELD_MAPPING.email}
          name={VALIDATION_FIELD_MAPPING.email}
          error={!!emailError}
          errorMessage={emailError}
          label={t('login.pageTexts.form.username.label')}
          placeholder={t('login.pageTexts.form.username.placeholder')}
          value={email}
          onKeyDown={handleKeyDown}
          onBlur={handleEmailBlur}
          onChange={handleEmailChange}
        />
      </div>
      {apiError && (
        <div className="mb-3">
          <p className="text-danger small mb-0">{apiError}</p>
        </div>
      )}
      <Button
        className="btn btn-primary d-grid w-100"
        text={loading ? t('login.pageTexts.otpMode.sending') : t('login.pageTexts.otpMode.sendCodeButton')}
        disabled={loading}
        loading={loading}
        onClick={handleSendCode}
      />
    </form>
  );
};

export default Form;
